/**
 * Best-effort detection of the current admin's country dial code from their
 * IP, used only to pre-fill the phone-number dropdown default -- never
 * anything that blocks or validates input. Falls back silently to null on
 * any failure (offline, ad-blocker, rate limit, slow network) so callers
 * must always have their own DEFAULT_COUNTRY_DIAL_CODE fallback.
 */
import countryCodeList from '@/JsonFiles/CountryCodeList.json'

const CACHE_KEY = 'yandle_detected_dial_code'
const FETCH_TIMEOUT_MS = 2500

let inFlight = null

function dialCodeForCountry(countryCode) {
  if (!countryCode) return null
  const match = (Array.isArray(countryCodeList) ? countryCodeList : []).find(
    (c) => String(c.code || '').toUpperCase() === String(countryCode).toUpperCase(),
  )
  return match?.dial_code || null
}

export async function detectDialCodeFromIp() {
  const cached = sessionStorage.getItem(CACHE_KEY)
  if (cached) return cached === 'null' ? null : cached

  if (inFlight) return inFlight

  inFlight = (async () => {
    try {
      const controller = new AbortController()
      const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
      const res = await fetch('https://ipapi.co/json/', { signal: controller.signal })
      clearTimeout(timer)
      if (!res.ok) throw new Error(`ipapi.co responded ${res.status}`)
      const data = await res.json()
      const dialCode = dialCodeForCountry(data?.country_code)
      sessionStorage.setItem(CACHE_KEY, dialCode ?? 'null')
      return dialCode
    } catch (error) {
      // Offline, blocked, or rate-limited -- caller's own default takes over.
      sessionStorage.setItem(CACHE_KEY, 'null')
      return null
    } finally {
      inFlight = null
    }
  })()

  return inFlight
}
